'use client';

import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
  HelpCircle, 
  Book, 
  LifeBuoy, 
  Keyboard,
  ExternalLink,
  Twitter,
  Mail,
  Phone,
  Clock,
  DollarSign,
  Gamepad2,
  Calendar,
  MapPin,
  Users,
  FileSpreadsheet
} from "lucide-react";
import { cn } from "@/lib/utils";

type HelpMenuItem =
  | {
      separator: true;
      icon?: never;
      label?: never;
      href?: never;
      external?: never;
    }
  | {
      separator?: false;
      icon: React.ComponentType<{ className?: string }>;
      label: string;
      href?: string;
      external?: boolean;
    };

const defaultHelpItems: HelpMenuItem[] = [
  { icon: Book, label: 'Getting Started', href: '/docs/getting-started' },
  { icon: Gamepad2, label: 'Adding Games', href: '/docs/adding-games' },
  { icon: FileSpreadsheet, label: 'Schedule Import', href: '/docs/schedule-import' },
  { icon: DollarSign, label: 'Payment Tracking', href: '/docs/payments' },
  { icon: Calendar, label: 'Calendar & Scheduling', href: '/docs/calendar' },
  { icon: MapPin, label: 'Location Management', href: '/docs/locations' },
  { icon: Users, label: 'Partner Management', href: '/docs/partners' },
  { separator: true },
  { icon: LifeBuoy, label: 'Contact Support', href: '/support' },
  { separator: true },
  { icon: Keyboard, label: 'Keyboard Shortcuts', href: '/docs/shortcuts' },
  { separator: true },
  { icon: Twitter, label: 'Follow @Officialspay', href: 'https://twitter.com/officialspay', external: true },
  { icon: ExternalLink, label: 'Status Page', href: 'https://status.officialspay.com', external: true },
];

interface HelpMenuProps {
  className?: string;
  items?: HelpMenuItem[];
  appName?: string;
  appVersion?: string;
  lastUpdated?: string;
}

export function HelpMenu({ 
  className,
  items = defaultHelpItems,
  appName = 'Officials Pay',
  appVersion = '1.2.0',
  lastUpdated = 'Updated 2 hours ago'
}: HelpMenuProps) {
  const [open, setOpen] = useState(false);

  const handleItemClick = (item: HelpMenuItem) => {
    if (item.href) {
      if (item.external) {
        window.open(item.href, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = item.href;
      }
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8", className)}
          title="Help & Documentation"
          data-help-menu
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="p-4">
          <h3 className="font-semibold text-sm mb-1">{appName}</h3>
          <p className="text-xs text-muted-foreground">
            Version {appVersion} • {lastUpdated}
          </p>
        </div>

        <Separator />

        <ScrollArea className="h-80">
          <div className="p-2">
            {items.map((item, index) => {
              if (item.separator) {
                return <Separator key={index} className="my-2" />;
              }

              return (
                <Button
                  key={index}
                  variant="ghost"
                  className="w-full justify-start h-8 px-2 text-sm font-normal"
                  onClick={() => handleItemClick(item)}
                >
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.label}
                  {item.external && (
                    <ExternalLink className="h-3 w-3 ml-auto" />
                  )}
                </Button>
              );
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
